import type { ReasonNode } from './types'

/**
 * The pulse clock — when a pass last touched each node, for the 1.5 s glow.
 *
 * This was `pulse()` on the store, which wrote `lastPulse` by replacing
 * `session.nodes` and bumping `rev`. A glow is animation, and sending it through
 * the session meant every whole-session subscriber re-rendered, every caption
 * reconciled, `childrenIndex` and `computeVisible` recomputed, every strand's
 * geometry was rebuilt (its cache is keyed on `rev`), the camera re-checked its
 * fit and an autosave was scheduled — about 10 ms of main thread per beat in
 * production and 75 ms in the dev build, once per arriving node, on top of the
 * growth animation the reader is watching at that moment. See finding 5 in
 * docs/performance-audit.md.
 *
 * So the clock lives beside `motion`: a module singleton the two renderers read
 * and nothing else does. A node's own `lastPulse` is still the floor — `makeNode`
 * stamps it at creation, an undo stamps the step's anchors, and a pass that
 * rewrites a node in place stamps it again through `patchNode` — so `of()` answers
 * with the later of the two. The one thing the clock gives up is a reload: a node
 * still glowing at the moment of a save comes back at rest, a 1.5 s tail behind a
 * 1.2 s debounce.
 *
 * Reset with the session, like `motion`: the demo's ids are literals, so a
 * replayed demo would otherwise inherit the previous one's glows.
 */
class PulseClock {
  private at = new Map<string, number>()

  /** A pass touched these nodes now. */
  mark(ids: Iterable<string>, now = Date.now()) {
    for (const id of ids) this.at.set(id, now)
  }

  /** When the node last pulsed: the clock's stamp or the node's own, whichever is later. */
  of(n: Pick<ReasonNode, 'id' | 'lastPulse'>): number {
    const t = this.at.get(n.id)
    return t !== undefined && t > n.lastPulse ? t : n.lastPulse
  }

  reset() {
    this.at.clear()
  }
}

export const pulses = new PulseClock()

/** How long a pulse glows. */
export const PULSE_MS = 1500

/**
 * How strongly a node glows `age` milliseconds after its pulse: 1 at the mark,
 * falling to 0 by `PULSE_MS` — and 0 at every age under reduced motion.
 *
 * The one reading both renderers draw a pulse from, and the one they ask when
 * they report whether a frame is still mid-transition. Each used to compute
 * `1 − age / PULSE_MS` inline and neither read its own `calm`: under reduced
 * motion a pulsed halo swelled to four times its reach and faded back over a
 * second and a half, the strand under it ran hot, and the loop held the full
 * rate to draw it — the one setting that asks for nothing to move. A pulse is
 * a glow that grows and dies away, so it has no still form to fall back on the
 * way growth does (drawn whole): it is off. Anything that must still say "this
 * one" under reduced motion says it with a mark that holds still.
 */
export function pulseLevel(age: number, calm: boolean): number {
  if (calm || !(age < PULSE_MS)) return 0
  return Math.min(1, 1 - age / PULSE_MS)
}
